import React, { FC, useCallback, useId, useMemo, useState } from 'react';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { StyledExploreLocations } from './ExploreLocations.styles';

export type LocationItemKind = 'region' | 'link';

export interface LocationItem {
  kind: LocationItemKind;
  /** Which column to render into (1 or 2) */
  column?: 1 | 2;
  /** Region label or link text */
  text: string;
  /** Link url (only for kind="link") */
  href?: string;
  /** Whether to open in new tab (only for kind="link") */
  openInNewTab?: boolean;
  /** Optional click track value */
  custom_click_track_value?: string;
}

export interface LocationGroup {
  title: string;
  items?: LocationItem[];
}

export const ExploreLocationsConfig = {
  emptyLabel: 'LoveTravelsExploreLocations',
  isEmpty: () => true,
};

export interface ExploreLocationsProps {
  heading?: string;
  initiallyExpanded?: boolean;
  locations?: LocationGroup[];
}

const normalizeColumn = (c: LocationItem['column']): 1 | 2 => (c === 2 ? 2 : 1);

const splitIntoColumns = (items: LocationItem[] = []) => {
  const col1: LocationItem[] = [];
  const col2: LocationItem[] = [];

  for (const item of items) {
    (normalizeColumn(item.column) === 1 ? col1 : col2).push(item);
  }

  return { col1, col2 };
};

const LinkItem: FC<{ item: LocationItem; index: number; columnPrefix: string }> = ({ item, index, columnPrefix }) => {
  const target = item.openInNewTab ? '_blank' : undefined;
  const rel = item.openInNewTab ? 'noopener noreferrer' : undefined;

  return (
    <li key={`${columnPrefix}-l-${index}`}>
      <a
        href={item.href || '#'}
        className={['location-item-link', item.custom_click_track_value ? 'custom_click_track' : ''].join(' ').trim()}
        data-custom_click_track_value={item.custom_click_track_value}
        target={target}
        rel={rel}
      >
        {item.text}
      </a>
    </li>
  );
};

export const ExploreLocationsComp: FC<ExploreLocationsProps> = ({
  heading = 'Explore All Participating Locations',
  initiallyExpanded = false,
  locations = [],
}) => {
  const rootId = useId();

  const [topExpanded, setTopExpanded] = useState<boolean>(!!initiallyExpanded);
  const [openLocationIndex, setOpenLocationIndex] = useState<number | null>(null);

  const toggleTop = useCallback(() => {
    setTopExpanded(v => !v);
  }, []);

  const toggleLocation = useCallback((index: number) => {
    setOpenLocationIndex(current => (current === index ? null : index));
  }, []);

  const hasLocations = locations && locations.length > 0;

  const topPanelId = useMemo(() => `${rootId}-locations-list`, [rootId]);

  return (
    <StyledExploreLocations className="explore-locations">
      <button
        type="button"
        className={['locations-list-accordion', topExpanded ? 'active' : 'not-active'].join(' ').trim()}
        aria-expanded={topExpanded}
        aria-controls={topPanelId}
        aria-label="Accordion"
        onClick={toggleTop}
      >
        <span className="lt-title-3">{heading}</span>
      </button>

      <div id={topPanelId} className="locations-list" style={{ display: topExpanded ? 'block' : 'none' }}>
        {!hasLocations
          ? null
          : locations.map((location, index) => {
              const panelId = `${rootId}-location-${index}`;
              const expanded = openLocationIndex === index;

              const { col1, col2 } = splitIntoColumns(location.items ?? []);
              const shouldRenderCol2 = col2.length > 0;

              return (
                <div className="location" key={`${location.title}-${index}`}>
                  <button
                    type="button"
                    className={['location-accordion', expanded ? 'active' : ''].join(' ').trim()}
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    aria-label="Accordion"
                    onClick={() => toggleLocation(index)}
                  >
                    <span>{location.title}</span>
                  </button>

                  <div
                    id={panelId}
                    className="row location-accordion-links"
                    style={{ display: expanded ? 'flex' : 'none' }}
                  >
                    <ul className="col-lg-6 location-item">
                      {col1.map((item, i) => {
                        if (item.kind === 'region') {
                          return (
                            <li className="location-item-region" key={`c1-r-${i}`}>
                              {item.text}
                            </li>
                          );
                        }

                        return <LinkItem key={`c1-l-${i}`} item={item} index={i} columnPrefix="c1" />;
                      })}
                    </ul>

                    {shouldRenderCol2 && (
                      <ul className="col-lg-6 location-item">
                        {col2.map((item, i) => {
                          if (item.kind === 'region') {
                            return (
                              <li className="location-item-region" key={`c2-r-${i}`}>
                                {item.text}
                              </li>
                            );
                          }

                          return <LinkItem key={`c2-l-${i}`} item={item} index={i} columnPrefix="c2" />;
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
      </div>
    </StyledExploreLocations>
  );
};

export const ExploreLocations = (props: ExploreLocationsProps) => {
  return (
    <div data-testid="explore-locations" data-component-name="o-prearrival-lovetravels-explore-locations">
      <EditableComponent config={ExploreLocationsConfig} {...props}>
        <ExploreLocationsComp {...props} />
      </EditableComponent>
    </div>
  );
};
